import BaseModel from "lib/model";
import BaseCollection from "lib/collection";
import LineItems from "collections/order/line-items";
import Globalize from "globalize";
import Meta from "models/meta";
import _ from "lodash";
import Ajax from "lib/ajax";
import url from "url";

const ShipmentItemModel = BaseModel.extend({
    props: {
        product: "objectId",
        sizes: "array",
    },
});

const ShipmentsItems = BaseCollection.extend({
    model: ShipmentItemModel,
});

const ShipmentInfo = BaseModel.extend({
    props: {
        type: "string",
        tracking_numbers: "array",
        status: "string",
        shipment_date: "utcDate",
    },
});

const ShipmentModel = BaseModel.extend({
    props: {
        type: "string",
        created_by: "string",
        created_on: "utcDate",
    },
    children: {
        shipment_info: ShipmentInfo,
    },
    collections: {
        line_items: ShipmentsItems,
    },
});

const Shipments = BaseCollection.extend({
    model: ShipmentModel,
});

const OrderSurchargeModel = BaseModel.extend({
    props: {
        name: "string",
        amount: "number",
    },
});

const OrderSurcharges = BaseCollection.extend({
    model: OrderSurchargeModel,
});

const AddressModel = BaseModel.extend({
    props: {
        __ref: "string",
        __display_name: { type: "string", required: true, allowNull: true },
        __line_1: { type: "string", required: true, allowNull: true },
        __line_2: "string",
        __line_3: "string",
        __line_4: "string",
        __city: "string",
        __phone: "string",
        __state: "string",
        __zip: "string",
        __country: {
            type: "string",
        },
        __code: "string",
        __first_name: "string",
        __last_name: "string",
    },
});

const Addresses = BaseModel.extend({
    children: {
        __billing: AddressModel,
        __shipping: AddressModel,
    },
});

const Documents = BaseModel.extend({
    props: {
        __admin_pdf: "string",
        __manager_pdf: "string",
        __rep_pdf: "string",
        __buyer_pdf: "string",
        __linesheet_pdf: "string",
    },
});

const Ref = BaseModel.extend({
    props: {
        __ref: { type: "objectId", required: true },
        __name: { type: "string", required: true },
    },
});

const PosExport = BaseModel.extend({
    props: {
        __pos_order_id: { type: "string", required: false },
        __pos_exported_on: { type: "utcDate", required: false },
    },
});

const ShippingPromotion = BaseModel.extend({
    props: {
        id: { type: "string", required: false },
        name: { type: "string", required: false },
        type: { type: "string", required: false },
        original_price: { type: "number", required: false },
    },
});

const ShippingInfo = BaseModel.extend({
    props: {
        rate_id: { type: "string", required: false },
        rate_type: { type: "string", required: false },
        carrier_id: { type: "string", required: false },
        package_type: { type: "string", required: false },
        zone: { type: "string", required: false },
        delivery_days: { type: "number", required: false },
        guaranteed_service: { type: "boolean", required: false },
        estimated_delivery_date: { type: "utcDate", required: false },
        carrier_delivery_days: { type: "string", required: false },
        ship_date: { type: "utcDate", required: false },
        negotiated_rate: { type: "boolean", required: false },
        service_type: { type: "string", required: false },
        service_code: { type: "string", required: false },
        trackable: { type: "boolean", required: false },
        validation_status: { type: "string", required: false },
        carrier_code: { type: "string", required: false },
        carrier_nickname: { type: "string", required: false },
        carrier_friendly_name: { type: "string", required: false },
        label: { type: "string", required: false },
        display_name: { type: "string", required: false },
        final_amount: { type: "number", required: false },
        formattedPrice: { type: "string", required: false },
        price: { type: "number", required: false },
    },
    children: {
        applied_promotion: ShippingPromotion,
    },
});

const PromotionAppliedModel = BaseModel.extend({
    props: {
        _id: { type: "string", required: false },
        name: { type: "string", required: false },
        description: { type: "string", required: false },
        discount: { type: "string", required: false },
        price_code: { type: "string", required: false },
    },
});

const PromotionsAppliedItem = BaseCollection.extend({
    model: PromotionAppliedModel,
});

const SurchargeModel = BaseModel.extend({
    props: {
        _id: { type: "string", required: true },
        name: { type: "string", required: true },
        description: { type: "string", required: false },
        price: { type: "string", required: true },
        currency_code: { type: "string", required: true },
    },
});

const SurchargesModel = BaseModel.extend({
    props: {
        total_surcharge: { type: "string", required: false },
    },
    collections: {
        applied: BaseCollection.extend({
            model: SurchargeModel,
        }),
    },
});

const CustomizationModel = BaseModel.extend({
    props: {
        customization_id: { type: "string", required: false },
    },
    children: {
        surcharges: SurchargesModel,
    },
});

const LineItemPromotionModel = BaseModel.extend({
    props: {
        total_applied: { type: "string", required: false },
        original_total: { type: "string", required: false },
        discounted_total: { type: "string", required: false },
        item_price_after_discount: { type: "string", required: false },
        adjusted_wholesale_string: { type: "string", required: false },
        company_manual_discount_on_item: { type: "string", required: false },
        item_manual_discount_on_item: { type: "string", required: false },
        subtotal: { type: "string", required: false },
        product: { type: "string", required: false },
        line_item_id: { type: "string", required: false },
    },
    children: {
        applied: PromotionsAppliedItem,
        surcharges: SurchargesModel,
    },
    collections: {
        customizations: BaseCollection.extend({
            model: CustomizationModel,
        }),
    },
});

const LineItemPromotionsItem = BaseCollection.extend({
    model: LineItemPromotionModel,
});

const ProductCustomizationSettings = BaseModel.extend({
    props: {
        prints: { type: "array", required: false },
        image_types: { type: "array", required: false },
    },
});

const LineItemPromotion = BaseModel.extend({
    props: {
        total_line_items_discount: { type: "number", required: false },
        total_after_line_items_discount: { type: "number", required: false },
    },
    collections: {
        line_items: LineItemPromotionsItem,
    },
});
const OrderPromotion = BaseModel.extend({
    props: {
        discounted_total: { type: "string", required: false },
        original_total: { type: "string", required: false },
        total_after_line_item_discount: { type: "string", required: false },
        total_applied: { type: "string", required: false },
        surcharge: { type: "string", required: false },
        company_manual_discount: { type: "string", required: false },
        total_order_level_discount: { type: "string", required: false },
        total_line_items_discount: { type: "string", required: false },
        total_line_item_manual_discounts: { type: "string", required: false },
        total_line_item_product_promotions: { type: "string", required: false },
        net_terms_extension: { type: "string", required: false },
    },
    collections: {
        applied: PromotionsAppliedItem,
    },
    children: {
        surcharges: SurchargesModel,
    },
});

const Promotions = BaseModel.extend({
    children: {
        __order_level_promotion: OrderPromotion,
        __line_item_promotion: LineItemPromotion,
    },
});

export default BaseModel.extend({
    props: {
        __brand: {
            __ref: { type: "string" },
            __name: { type: "string" },
        },
        __promotion_names: { type: "array", required: false },
        __schema_id: { type: "objectId", required: true },
        __order_group_id: { type: "objectId", required: true },
        __locked: { type: "boolean", required: true, default: false },
        __lookups: { type: "array", required: false },
        __auto_number: { type: "number" },
        __order_number: { type: "string", required: true },
        __currency_code: { type: "string", required: true },
        __collaborative: { type: "boolean", required: false },
        __customer_po_number: { type: "string" },
        __status: {
            type: "string",
            required: true,
            default: "draft",
            values: [
                "ezorder",
                "working",
                "draft",
                "review",
                "pending",
                "approved",
                "processed",
                "shipped",
                "cancelled",
            ],
        },
        __status_name: { type: "string" },
        __draft_type: {
            type: "string",
            values: ["draft", "easy_order", "edit"],
        },
        __title: { type: "string" },
        __subteam: { type: "string" },
        __signature: { type: "string" },

        __ship_start: { type: "utcDate" },
        __ship_end: { type: "utcDate" },
        __edited_on: { type: "utcDate" },
        __easy_order_expiration: { type: "utcDate" },
        __discount: { type: "number", required: true, default: 0 },
        __additional_percentage: { type: "number", required: true, default: 0 },
        __additional_percentage_label: { type: "string", required: true, default: "" },
        __total: { type: "currency", required: true, default: 0 },
        __total_quantity: { type: "number", required: true, default: 0 },
        __amount_paid: { type: "number", required: true, default: 0 },
        __payment_status: {
            type: "string",
            required: false,
            default: "Not Paid",
            values: ["Not Paid", "Partial Payment", "Paid in Full"],
        },
        __last_transaction_status: { type: "string" },
        __notes: { type: "string" },
        __nuorder_card_id: { type: "objectId", required: true },
        has_card_on_file: { type: "string" },

        __includes_cancelled: { type: "boolean" },
        __cancelled_units: { type: "number", default: 0 },
        __cancelled_total: { type: "currency", default: 0 },
        __shipment_status: "array",
        is_drop_ship: { type: "boolean" },
        __requested_payment_date: { type: "utcDate" },
        __signed: { type: "boolean", default: false },
        original_order_id: { type: "objectId" },
        draft_name: { type: "string" },
        __product_customization_templates: { type: "array", required: false },
        __nuorder_payment_method_type_label: { type: "string" },
    },
    derived: {
        __products_amount: {
            deps: ["__line_items"],
            fn: function () {
                return this.__line_items.size();
            },
        },
        __cancelled_amount: {
            deps: ["__line_items", "__line_items.cancelled"],
            fn: function () {
                return _.size(this.__line_items.filter("cancelled"));
            },
        },
    },
    children: {
        __addresses: Addresses,
        __documents: Documents,
        __creator: Ref,
        __rep: Ref,
        __company: Ref,
        __buyer: Ref,
        __meta: Meta,
        __shipping_information: ShippingInfo,
        __pos_export: PosExport,
        __promotions: Promotions,
        __product_customization_settings: ProductCustomizationSettings,
    },
    collections: {
        __line_items: LineItems,
        order_surcharges: OrderSurcharges,
        __shipments: Shipments,
    },
    urlRoot: function () {
        return "/api/v3.0/order/";
    },
    getCurrencyCode: function (scope) {
        if (this.__currency_code) {
            return this.__currency_code;
        }
        if (scope.brand && scope.brand._id) {
            return scope.brand.settings.default_currency || "USD";
        }
        return "USD";
    },
    getSafeCurrencyCode: function (scope) {
        const currency = this.getCurrencyCode(scope);
        switch (currency) {
            case "USI":
                return "USD";
            case "GGP":
                return "GBP";
            default:
                return currency || "USD";
        }
    },
    isDraftOrder: function () {
        return this.__status === "draft" && this.__draft_type === "draft";
    },

    sortDraftLineItems: function () {
        const groupedByDelivery = _.groupBy(this.__line_items.models, (item) => {
            const shipStart = item.__ship_start.format("MMM DD, YYYY");
            const shipEnd = item.__ship_end.format("MMM DD, YYYY");
            return shipStart + " - " + shipEnd;
        });

        return groupedByDelivery;
    },
    shouldShowBuyerVisibilityWarning: function () {
        return this.isDraftOrder() && !this.__collaborative && this.__buyer && this.__buyer.__ref;
    },
    shouldShowBuyerVisibilityConfirmation: function () {
        return this.isDraftOrder() && this.__collaborative && this.__buyer && this.__buyer.__ref;
    },
    resendOrderEmail: function ({ onSuccess, onError }) {
        const actionModel = this.__meta.__actions.find((action) => action.__key === "__email");
        Ajax.sync("read", this, {
            url: url.parse(actionModel.__uri).pathname,
            success: () => {
                if (onSuccess) {
                    if (this.isDraftOrder()) {
                        this.set("__collaborative", true);
                    }
                    onSuccess();
                }
            },
            error: (rawResponse, status, response) => {
                if (onError) {
                    onError(response);
                }
            },
        });
    },

    hasCancelledStyles: function () {
        return (
            !!this.__includes_cancelled &&
            !["shipped", "cancelled"].includes(this.__status) &&
            (this.__status === "draft" || this.__ship_end.isSameOrAfter(/*now*/))
        );
    },

    isExportedToPOS: function () {
        return this.__pos_export?.__pos_order_id
            ? Globalize.formatMessage("Exported")
            : Globalize.formatMessage("Not Exported");
    },

    getSignedValue: function () {
        if (this.__status === "draft" || this.__status === "ezorder") {
            return null;
        } else {
            return this.__signed ? Globalize.formatMessage("Yes") : Globalize.formatMessage("No");
        }
    },
});
